import { useCallback } from 'react';
import {
  useGetApiWorkflowsTypesQuery,
  type HandlersWorkflowTypeWithSigning,
} from '../app/athenaApiGen';

export type SignatureRequirementQuery = {
  // Workflow type name as it appears in the catalog (e.g. the IMCS TAR
  // With/Without APM types). Matched case-insensitively against the catalog's
  // `workflowType`.
  workflowType?: string;
  // The task being approved/rejected. Used to pick the per-task requirement
  // entry; when omitted or unmatched we fall back to "any required entry on
  // the type".
  taskNumber?: number;
};

// signatureRequiredFor decides whether a workflow task requires a cryptographic
// signature, driven entirely by the backend signing policy surfaced on
// GET /api/workflows/types (`signingRequirements[].required`). This replaces
// the old isTarWorkflow() regex: the backend emptied its
// signingRequiredWorkflowTypes set in services-0.18.2 (#1255), so every entry
// is currently `required: false` and approvals take the non-signed pipeline.
// When product re-enables signing for a workflow type (#1179 promotes this to
// the WorkflowSigningRequirement table), the prompt follows automatically with
// no frontend change.
//
// Backend shape (services/internal/signing/requirements.go RequirementsForTasks):
// one TypedRequirement per task number, `applies: "*"`, `required` gated on the
// workflow type's policy.
export function signatureRequiredFor(
  catalog: HandlersWorkflowTypeWithSigning[] | undefined,
  query: SignatureRequirementQuery,
): boolean {
  const workflowType = query.workflowType?.trim();
  if (!catalog || !workflowType) return false;

  const entry = catalog.find(
    (t) => t.workflowType?.trim().toLowerCase() === workflowType.toLowerCase(),
  );
  const requirements = entry?.signingRequirements;
  if (!requirements?.length) return false;

  // Prefer the requirement scoped to the specific task. If the task number is
  // unknown or the type has no dedicated entry for it, treat the task as
  // requiring a signature when ANY entry on the type is `required` — fail
  // toward signing rather than silently skipping it.
  const match = requirements.find((r) => r.taskNumber === query.taskNumber);
  if (match) return match.required === true;
  return requirements.some((r) => r.required === true);
}

// useSignatureRequirement reads the (cached) workflow-types catalog and returns
// a predicate that callers use to gate the signing flow per (workflowType,
// taskNumber). The underlying query is shared across consumers via RTK Query's
// cache, so calling this hook in multiple panels does not refetch.
export function useSignatureRequirement() {
  const { data: catalog, isLoading } = useGetApiWorkflowsTypesQuery({});

  const isSignatureRequired = useCallback(
    (query: SignatureRequirementQuery) => signatureRequiredFor(catalog, query),
    [catalog],
  );

  return { isSignatureRequired, isLoading };
}
